import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Phone, ChevronDown, PhoneCall, CalendarClock, ListChecks, FilePlus2, ExternalLink, Link2, BarChart3, Users, DollarSign, TrendingUp, FileText, Calendar, Star, Target, StickyNote, FolderKanban, Layers, Building2 } from 'lucide-react';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import { ScheduleCallModal } from './ScheduleCallModal';
import { AddToCrmModal } from './AddToCrmModal';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import { SearchBarbourModal } from './SearchBarbourModal';
import Quotes from '../pages/Quotes';
import { useZohoSession } from '../contexts/ZohoSessionContext';
import { toast } from 'sonner';
import InBody from '../v3/InBody';

const CRM_PRESETS = [
  { id: 'link',     Icon: Link2,       color: '#6b7280' },
  { id: 'chart',    Icon: BarChart3,   color: '#2563eb' },
  { id: 'phone',    Icon: Phone,       color: '#16a34a' },
  { id: 'users',    Icon: Users,       color: '#7c3aed' },
  { id: 'dollar',   Icon: DollarSign,  color: '#ea580c' },
  { id: 'trending', Icon: TrendingUp,  color: '#0891b2' },
  { id: 'file',     Icon: FileText,    color: '#d97706' },
  { id: 'calendar', Icon: Calendar,    color: '#dc2626' },
  { id: 'star',     Icon: Star,        color: '#ca8a04' },
  { id: 'target',   Icon: Target,      color: '#be185d' },
];

const renderCrmIcon = (iconUrl) => {
  if (!iconUrl) return <Link2 className="w-3.5 h-3.5 text-gray-500" />;
  if (iconUrl.startsWith('data:')) return <img src={iconUrl} alt="" className="w-4 h-4 object-contain rounded" />;
  if (iconUrl.startsWith('preset:')) {
    const p = CRM_PRESETS.find(x => x.id === iconUrl.slice(7));
    if (p) return <p.Icon className="w-3.5 h-3.5" style={{ color: p.color }} />;
  }
  return <img src={iconUrl} alt="" className="w-4 h-4 object-contain" onError={e => { e.target.style.display = 'none'; }} />;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Pill styling per user spec — WHITE background, with a light-blue → blue
// gradient border AND text. We achieve the gradient border by stacking two
// linear-gradient backgrounds on the button (one white, one the gradient)
// with `background-origin/clip` set so only the border area shows the
// gradient. Gradient text is done via `background-clip: text`.
const PILL_GRADIENT_LIGHT = '#60a5fa';   // tailwind blue-400
const PILL_GRADIENT_DARK = '#2563eb';    // tailwind blue-600
const PILL_GRADIENT = `linear-gradient(135deg, ${PILL_GRADIENT_LIGHT} 0%, ${PILL_GRADIENT_DARK} 100%)`;

// New Manage-CRM icon supplied by the user. Hosted on the asset CDN; we fall
// back to the lucide Phone icon if the image fails to load (e.g. asset not
// yet available, offline preview, etc.).
const MANAGE_CRM_ICON_URL = 'https://customer-assets.emergentagent.com/job_2672dfd1-c941-42e7-9d92-fed957be81ff/artifacts/bliatxai_IMG_2023.png';

/**
 * Dropdown pill that exposes three quick actions:
 *   1. Add Call — opens AddCallToCrmModal (log a completed call)
 *   2. Schedule Call — opens ScheduleCallModal (book a future call)
 *   3. Manage Calls — routes to /manage-calls
 *
 * Only rendered when the current user is mapped to a Zoho CRM seat
 * (or is a super-admin). This keeps the nav clean for the 100+ C30 users
 * who don't work in the CRM dashboard.
 *
 * We always fetch `/zoho/my-status` fresh on mount — no localStorage cache —
 * because localStorage is per-browser not per-user, and a stale "not linked"
 * value could survive a session change and hide the pill from a legitimate
 * CRM user. The endpoint itself is cheap (one DB read + a cached Zoho-user
 * lookup) so there's no benefit to persisting its result.
 */
// `action` (optional) makes this ONE item of the drop-down a link of its own (Settings > User Interface > User Spaces > Links > Add Link > Single link): the button does what the item does, and
// everything else here (the CRM access check, the windows, the chaining from a call to a quote and back) works exactly as it does inside the drop-down. `actionLabel` is its name.
export const LogCallDropdownPill = ({ buttonClassName = '', user = null, mobileMode = false, shape = 'pill', icon: OverrideIcon = null, action = null, actionLabel = '' }) => {
  const navigate = useNavigate();
  const cornerClass = shape === 'oblong' ? 'rounded-xl' : 'rounded-full';
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showAddCall, setShowAddCall] = useState(false);
  const [showScheduleCall, setShowScheduleCall] = useState(false);
  // Fast-track "Add Quote / Project" state — silently parses the chosen PDF
  // and opens the existing AddToCrmModal pre-loaded with the parsed account
  // name + ex-VAT subtotal. The Quote Editor is intentionally skipped.
  const [showAddToCrm, setShowAddToCrm] = useState(false);
  const [showContactNotesTasks, setShowContactNotesTasks] = useState(false);
  const [showSearchBarbour, setShowSearchBarbour] = useState(false);
  const [showSearchBarbourV2, setShowSearchBarbourV2] = useState(false);
  // Standard Quotes modal — opened when chaining call → quote
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [chainedQuoteCustomerName, setChainedQuoteCustomerName] = useState('');
  const [fastTrackForm, setFastTrackForm] = useState(null);
  const [fastTrackPdf, setFastTrackPdf] = useState(null);
  const [fastTrackSubtotal, setFastTrackSubtotal] = useState(0);
  const [fastTrackProcessing, setFastTrackProcessing] = useState(false);
  // Chain-handoff: when a rep clicks "Add & Then Quote" on the call modal
  // (or "Create & Then Call" on the quote modal) we stash the linked
  // account/contact here so the next modal can pre-fill them. The next
  // modal opens immediately after the first one closes.
  const [chainedQuoteContext, setChainedQuoteContext] = useState(null);
  const [chainedCallContext, setChainedCallContext] = useState(null);
  const fileInputRef = useRef(null);
  const [iconBroken, setIconBroken] = useState(false);
  const [status, setStatus] = useState(null);   // null = loading
  const [crmLinks, setCrmLinks] = useState([]);
  const [barbourUseDirectApi, setBarbourUseDirectApi] = useState(true); // default true until fetched, matches backend default
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`${API}/zoho/my-status`)
      .then(r => { if (!cancelled) setStatus(r.data || { is_linked: false, is_super_admin: false }); })
      .catch(() => { if (!cancelled) setStatus({ is_linked: false, is_super_admin: false }); });
    api.get(`${API}/crm-links`)
      .then(r => { if (!cancelled) setCrmLinks(r.data?.links || []); })
      .catch(() => {});
    // Whether "Search Barbour" (V1) should even be offered — hidden when a
    // super admin has toggled the whole app onto the Zoho-function method
    // (Settings > Integrations > Barbour > "Use Direct Barbour API").
    api.get(`${API}/barbour/use-direct-api`)
      .then(r => { if (!cancelled) setBarbourUseDirectApi(r.data?.use_direct_api !== false); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const { requireConfirmed } = useZohoSession();

  // ── Determine CRM access ────────────────────────────────────────────────────
  // Always render the pill so it's visible to every user. When the user isn't
  // linked to CRM (or while the status is still loading), show a greyed-out,
  // non-clickable version so they can see the feature exists.
  const isAccessible = !!status && (status.is_linked || status.is_super_admin);

  // Gate each dropdown action. If the user hasn't confirmed Zoho login for
  // this browser session, the gate modal pops instead of the target flow.
  const guard = (fn) => () => {
    if (!requireConfirmed()) return;
    fn();
  };

  // Trigger the fast-track flow: open the AddToCrmModal directly.
  const handleAddQuoteProject = () => {
    setOpen(false);
    setMobileOpen(false);
    setShowAddToCrm(true);
  };

  // Legacy silent-parse helper — kept for any future flow.
  const handleFastTrackFile = async (e) => {
    e.target.value = '';
  };

  // ── Mobile accordion mode — greyed-out when no access ───────────────────────
  if (mobileMode) {
    if (!isAccessible) {
      return (
        <div
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-not-allowed opacity-50 select-none"
          title="CRM access not enabled for your account"
          data-testid="mobile-nav-manage-crm-disabled"
        >
          <img src={MANAGE_CRM_ICON_URL} alt="" onError={() => {}} className="w-5 h-5 object-contain flex-shrink-0 grayscale" />
          <span className="font-medium text-sm flex-1 text-gray-400">Sales / CRM</span>
          <ChevronDown className="w-4 h-4 text-gray-300" />
        </div>
      );
    }
    return (
      <>
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          data-testid="mobile-nav-manage-crm"
        >
          <img src={MANAGE_CRM_ICON_URL} alt="" onError={() => {}} className="w-5 h-5 object-contain flex-shrink-0" />
          <span className="font-medium text-sm flex-1">Sales / CRM</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpen ? 'rotate-180' : ''}`} style={{ color: '#2563eb' }} />
        </button>

        {mobileOpen && (
          <div className="ml-4 space-y-1">
            <button onClick={guard(() => { navigate('/sales-hub'); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-amber-50 transition-colors text-left" data-testid="mobile-crm-sales-hub">
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><TrendingUp className="w-3.5 h-3.5 text-amber-600" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Sales Hub</div><div className="text-[11px] text-gray-500">Sales &amp; Reports</div></div>
            </button>
            <div className="my-1 border-t border-gray-300" />
            <button onClick={guard(() => { setShowContactNotesTasks(true); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-orange-50 transition-colors text-left" data-testid="mobile-crm-customer-mgmt">
              <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0"><StickyNote className="w-3.5 h-3.5 text-orange-600" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Customer Management</div><div className="text-[11px] text-gray-500">Contacts, notes, tasks &amp; quotes</div></div>
            </button>
            <div className="my-1 border-t border-gray-300" />
            <button onClick={guard(() => { handleAddQuoteProject(); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-emerald-50 transition-colors text-left" data-testid="mobile-crm-add-quote-project">
              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0"><FilePlus2 className="w-3.5 h-3.5 text-emerald-700" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Add Project / Quote</div><div className="text-[11px] text-gray-500">Add direct to CRM</div></div>
            </button>
            <div className="my-1 border-t border-gray-300" />
            {/* Search Barbour (V1, direct API) — hidden when the app-wide toggle is off */}
            {barbourUseDirectApi && (
              <button onClick={guard(() => { setShowSearchBarbour(true); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-cyan-50 transition-colors text-left" data-testid="mobile-crm-search-barbour">
                <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-3.5 h-3.5 text-cyan-600" /></div>
                <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Search Barbour</div><div className="text-[11px] text-gray-500">Find projects near a postcode</div></div>
              </button>
            )}
            <div className="my-1 border-t border-gray-300" />
            <button onClick={guard(() => { setShowSearchBarbourV2(true); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-cyan-50 transition-colors text-left" data-testid="mobile-crm-search-barbour-v2">
              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0"><Building2 className="w-3.5 h-3.5 text-cyan-600" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Barbour Search V2</div><div className="text-[11px] text-gray-500">Via the Zoho CRM function</div></div>
            </button>
            <div className="my-1 border-t border-gray-300" />
            <button onClick={guard(() => { setShowAddCall(true); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-blue-50 transition-colors text-left" data-testid="mobile-crm-add-call">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0"><PhoneCall className="w-3.5 h-3.5 text-blue-600" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Add Call</div><div className="text-[11px] text-gray-500">Log a completed call</div></div>
            </button>
            <button onClick={guard(() => { navigate('/manage-calls'); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors text-left" data-testid="mobile-crm-manage-calls">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0"><ListChecks className="w-3.5 h-3.5 text-gray-700" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Manage Calls</div><div className="text-[11px] text-gray-500">View, edit &amp; reschedule</div></div>
            </button>
            <button onClick={guard(() => { setShowScheduleCall(true); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-purple-50 transition-colors text-left" data-testid="mobile-crm-schedule-call">
              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0"><CalendarClock className="w-3.5 h-3.5 text-purple-600" /></div>
              <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Schedule Call</div><div className="text-[11px] text-gray-500">Book for you or a teammate</div></div>
            </button>
            <div className="my-1 border-t border-gray-300" />
            {(status?.is_super_admin || user?.can_view_project_tracking) && (
              <button onClick={guard(() => { navigate('/project-tracking'); setMobileOpen(false); })} className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-blue-50 transition-colors text-left" data-testid="mobile-crm-project-tracking">
                <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0"><FolderKanban className="w-3.5 h-3.5 text-blue-600" /></div>
                <div className="min-w-0"><div className="text-sm font-medium text-gray-900">Project Tracking</div></div>
              </button>
            )}
            {crmLinks.length > 0 && (
              <>
                <div className="pt-1 pb-0.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">CRM Links</div>
                {crmLinks.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-200 hover:bg-blue-50 transition-colors" data-testid={`mobile-crm-link-${link.id}`}>
                    <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">{renderCrmIcon(link.icon_url)}</div>
                    <span className="text-sm text-gray-800 truncate flex-1">{link.name}</span>
                    <ExternalLink className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </>
            )}
          </div>
        )}

        {/* All modals */}
        <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleFastTrackFile} data-testid="log-call-fast-track-file-input" />
        <AddCallToCrmModal isOpen={showAddCall} onClose={() => { setShowAddCall(false); setChainedQuoteContext(null); setChainedCallContext(null); }} prefillAccount={chainedQuoteContext}
          onChainToQuote={(ctx) => {
            setShowAddCall(false);
            setChainedCallContext(null);
            setChainedQuoteContext(null);
            setChainedQuoteCustomerName(ctx?.account?.name || '');
            setShowQuoteModal(true);
          }} />
        <ScheduleCallModal isOpen={showScheduleCall} onClose={() => setShowScheduleCall(false)} />
        {showAddToCrm && (
          <AddToCrmModal isOpen={showAddToCrm} onClose={() => { setShowAddToCrm(false); setChainedCallContext(null); setChainedQuoteContext(null); }}
            quoteForm={fastTrackForm || (chainedCallContext ? { customer_name: chainedCallContext.account?.name || '', customer_account_number: chainedCallContext.account?.zoho_account_number || '', zoho_account_id: chainedCallContext.account?.id || '', contact_id: chainedCallContext.contact?.id || '', contact_name: chainedCallContext.contact?.name || '' } : null)}
            pdfFile={fastTrackPdf || null} subtotal={fastTrackSubtotal || 0} fastTrack={true}
            onChainToCall={(ctx) => { setChainedQuoteContext(ctx); setShowAddToCrm(false); setShowAddCall(true); }} />
        )}
        <ContactNotesTasksModal isOpen={showContactNotesTasks} onClose={() => setShowContactNotesTasks(false)} />
        <SearchBarbourModal isOpen={showSearchBarbour} onClose={() => setShowSearchBarbour(false)} />
        <SearchBarbourModal
          isOpen={showSearchBarbourV2}
          onClose={() => setShowSearchBarbourV2(false)}
          apiPath="/barbour/search-v2"
          title="Barbour Search V2"
          subtitle="Find Barbour ABI projects via the Zoho CRM function"
          testIdPrefix="search-barbour-v2"
        />
        {showQuoteModal && Quotes.Modal && (
          <Quotes.Modal
            isOpen={showQuoteModal}
            onClose={() => { setShowQuoteModal(false); setChainedQuoteCustomerName(''); }}
            mode="recent"
            prefillCustomer={chainedQuoteCustomerName}
          />
        )}
      </>
    );
  }

  // The items of the drop-down that can be a link of their own (frontend/src/utils/navHeaderRegistry.js, BUILTIN_ACTIONS), and what each does.
  const ACTIONS = {
    crm_sales_hub: { Icon: TrendingUp, run: () => navigate('/sales-hub') },
    crm_customer_management: { Icon: StickyNote, run: () => setShowContactNotesTasks(true) },
    crm_add_quote_project: { Icon: FilePlus2, run: handleAddQuoteProject },
    crm_search_barbour: { Icon: Building2, run: () => setShowSearchBarbour(true), hidden: !barbourUseDirectApi },
    crm_search_barbour_v2: { Icon: Building2, run: () => setShowSearchBarbourV2(true) },
    crm_add_call: { Icon: PhoneCall, run: () => setShowAddCall(true) },
    crm_manage_calls: { Icon: ListChecks, run: () => navigate('/manage-calls') },
    crm_schedule_call: { Icon: CalendarClock, run: () => setShowScheduleCall(true) },
    crm_project_tracking: { Icon: FolderKanban, run: () => navigate('/project-tracking'), needsTracking: true },
    crm_spellers: { Icon: Layers, run: () => navigate('/spellers'), needsTracking: true },
  };
  const actionDef = action ? ACTIONS[action] : null;
  const actionShown = !!actionDef && !actionDef.hidden && (!actionDef.needsTracking || !!status?.is_super_admin || !!user?.can_view_project_tracking);
  const ActionIcon = OverrideIcon || (actionDef ? actionDef.Icon : Phone);
  // Not linked to CRM: shown greyed out and not clickable, as the drop-down is.
  const actionButton = actionShown ? (
    <button
      type="button"
      data-testid={`nav-action-${action}`}
      data-v3-open={(showAddCall || showScheduleCall || showAddToCrm || showContactNotesTasks || showSearchBarbour || showSearchBarbourV2 || showQuoteModal) ? 'true' : undefined}
      disabled={!isAccessible}
      title={isAccessible ? undefined : 'CRM access not enabled for your account'}
      onClick={guard(actionDef.run)}
      className={`h-9 flex items-center gap-1.5 px-3 rounded-xl border-2 bg-white whitespace-nowrap transition-all duration-200 ${isAccessible ? 'hover:shadow-md hover:scale-105' : 'cursor-not-allowed opacity-50'}`}
      style={{ borderColor: isAccessible ? PILL_GRADIENT_DARK : '#d1d5db' }}
    >
      <ActionIcon className="w-3.5 h-3.5" style={{ color: isAccessible ? PILL_GRADIENT_DARK : '#9ca3af' }} />
      <span className="font-semibold text-[11px] lg:text-xs" style={{ color: isAccessible ? PILL_GRADIENT_DARK : '#9ca3af' }}>{actionLabel}</span>
    </button>
  ) : null;

  // ── Desktop pill mode ─────────────────────────────────────────────────────────
  return (
    <>
      {action ? actionButton : (
      <div className="relative" ref={ref}>
        {/* Greyed-out pill for users without CRM access */}
        {!isAccessible ? (
          <button
            data-testid="log-call-dropdown-pill-disabled"
            disabled
            className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 ${cornerClass} whitespace-nowrap cursor-not-allowed opacity-50 ${buttonClassName}`}
            style={{ background: 'white', border: '2px solid #d1d5db' }}
            title="CRM access not enabled for your account"
          >
            {iconBroken ? (
              <Phone className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4 text-gray-400" />
            ) : (
              <img
                src={MANAGE_CRM_ICON_URL}
                alt=""
                onError={() => setIconBroken(true)}
                className="w-3.5 h-3.5 lg:w-4 lg:h-4 xl:w-5 xl:h-5 object-contain grayscale"
              />
            )}
            <span className="font-semibold text-[11px] lg:text-xs xl:text-sm text-gray-400">
              Sales / CRM
            </span>
            <ChevronDown className="w-3.5 h-3.5 text-gray-300" />
          </button>
        ) : (
        <button
          data-testid="log-call-dropdown-pill"
          data-v3-open={(showAddCall || showScheduleCall || showAddToCrm || showContactNotesTasks || showSearchBarbour || showSearchBarbourV2 || showQuoteModal) ? 'true' : undefined}
          onClick={() => setOpen(v => !v)}
          className={`h-9 flex items-center gap-1 lg:gap-1.5 xl:gap-2 px-2 lg:px-3 xl:px-6 ${cornerClass} hover:shadow-lg transition-all duration-200 hover:scale-105 whitespace-nowrap ${buttonClassName}`}
          style={{
            background: `linear-gradient(white, white) padding-box, ${PILL_GRADIENT} border-box`,
            border: '2px solid transparent',
          }}
        >
          {OverrideIcon && <OverrideIcon className="v3-own-icon w-3.5 h-3.5" />}
          {iconBroken ? (
            <Phone className="w-3 h-3 lg:w-3.5 lg:h-3.5 xl:w-4 xl:h-4" style={{ color: PILL_GRADIENT_DARK }} />
          ) : (
            <img
              src={MANAGE_CRM_ICON_URL}
              alt=""
              onError={() => setIconBroken(true)}
              className="w-3.5 h-3.5 lg:w-4 lg:h-4 xl:w-5 xl:h-5 object-contain"
            />
          )}
          <span
            className="font-semibold text-[11px] lg:text-xs xl:text-sm"
            style={{
              backgroundImage: PILL_GRADIENT,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            Sales / CRM
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
            style={{ color: PILL_GRADIENT_DARK }}
          />
        </button>
        )}

        {open && (
          <div
            className="absolute z-50 mt-2 w-64 bg-white border border-gray-200 rounded-2xl shadow-xl py-1.5 left-1/2 -translate-x-1/2"
            data-testid="log-call-dropdown-menu"
          >
            {/* Sales Hub */}
            <button
              onClick={guard(() => { navigate('/sales-hub'); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-amber-50 transition-colors"
              data-testid="log-call-option-sales-hub"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-amber-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Sales Hub</div>
                <div className="text-[11px] text-gray-500">Sales &amp; Reports</div>
              </div>
            </button>

            {/* Separator */}
            <div className="mx-3 my-1 border-t border-gray-300" />

            {/* Contact / Notes / Tasks */}
            <button
              onClick={guard(() => { setShowContactNotesTasks(true); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-orange-50 transition-colors"
              data-testid="log-call-option-contact-notes-tasks"
            >
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <StickyNote className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Customer Management</div>
                <div className="text-[11px] text-gray-500">Contacts, notes, tasks &amp; quotes</div>
              </div>
            </button>

            {/* Separator */}
            <div className="mx-3 my-1 border-t border-gray-300" />

            {/* Add Project / Quote */}
            <button
              onClick={guard(handleAddQuoteProject)}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-emerald-50 transition-colors"
              data-testid="log-call-option-add-quote-project"
            >
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <FilePlus2 className="w-4 h-4 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Add Project / Quote</div>
                <div className="text-[11px] text-gray-500">Add direct to CRM</div>
              </div>
            </button>

            {/* Separator */}
            <div className="mx-3 my-1 border-t border-gray-300" />

            {/* Search Barbour (V1, direct API) — hidden when the app-wide toggle is off */}
            {barbourUseDirectApi && (
              <button
                onClick={guard(() => { setShowSearchBarbour(true); setOpen(false); })}
                className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-cyan-50 transition-colors"
                data-testid="log-call-option-search-barbour"
              >
                <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-cyan-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">Search Barbour</div>
                  <div className="text-[11px] text-gray-500">Find projects near a postcode</div>
                </div>
              </button>
            )}

            {/* Search Barbour V2 (via the Zoho CRM function) */}
            <button
              onClick={guard(() => { setShowSearchBarbourV2(true); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-cyan-50 transition-colors"
              data-testid="log-call-option-search-barbour-v2"
            >
              <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Barbour Search V2</div>
                <div className="text-[11px] text-gray-500">Via the Zoho CRM function</div>
              </div>
            </button>

            {/* Separator */}
            <div className="mx-3 my-1 border-t border-gray-300" />

            <button
              onClick={guard(() => { setShowAddCall(true); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors"
              data-testid="log-call-option-add"
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <PhoneCall className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Add Call</div>
                <div className="text-[11px] text-gray-500">Log a completed call now</div>
              </div>
            </button>
            <button
              onClick={guard(() => { navigate('/manage-calls'); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              data-testid="log-call-option-manage"
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <ListChecks className="w-4 h-4 text-gray-700" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Manage Calls</div>
                <div className="text-[11px] text-gray-500">View, edit & reschedule</div>
              </div>
            </button>
            <button
              onClick={guard(() => { setShowScheduleCall(true); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-purple-50 transition-colors"
              data-testid="log-call-option-schedule"
            >
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <CalendarClock className="w-4 h-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Schedule Call</div>
                <div className="text-[11px] text-gray-500">Book one for you or a teammate</div>
              </div>
            </button>
            <div className="mx-3 my-0.5 border-t border-gray-300" />
            {(status?.is_super_admin || user?.can_view_project_tracking) && (
            <button
              onClick={guard(() => { navigate('/project-tracking'); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-blue-50 transition-colors"
              data-testid="log-call-option-project-tracking"
            >
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                <FolderKanban className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Project Tracking</div>
                <div className="text-[11px] text-gray-500">Monitor CRM projects & packages</div>
              </div>
            </button>
            )}
            {(status?.is_super_admin || user?.can_view_project_tracking) && (
            <button
              onClick={guard(() => { navigate('/spellers'); setOpen(false); })}
              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-orange-50 transition-colors"
              data-testid="log-call-option-spellers"
            >
              <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                <Layers className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Spellers</div>
                <div className="text-[11px] text-gray-500">Speller Metcalfe targeting tracker</div>
              </div>
            </button>
            )}

            {/* CRM Links section */}
            {crmLinks.length > 0 && (
              <>
                <div className="mx-3 my-1 border-t border-gray-300" />
                <div className="px-4 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">CRM Links</div>
                {crmLinks.map(link => {
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      className="w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-blue-50 transition-colors group"
                      data-testid={`crm-link-${link.id}`}
                    >
                      <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {renderCrmIcon(link.icon_url)}
                      </div>
                      <span className="text-sm font-medium text-gray-800 truncate group-hover:text-blue-700">{link.name}</span>
                      <ExternalLink className="w-3 h-3 text-gray-400 ml-auto flex-shrink-0" />
                    </a>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
      )}

      {/* Hidden file input for the fast-track Add Quote / Project flow. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleFastTrackFile}
        data-testid="log-call-fast-track-file-input"
      />

      {/* Inside UI V3 these windows are drawn on the page body, not inside the left bar (see InBody). */}
      <InBody>
      <AddCallToCrmModal
        isOpen={showAddCall}
        onClose={() => { setShowAddCall(false); setChainedQuoteContext(null); setChainedCallContext(null); }}
        prefillAccount={chainedQuoteContext}
        onChainToQuote={(ctx) => {
          // Call has been logged — open the standard Quotes modal with customer pre-filled
          setShowAddCall(false);
          setChainedCallContext(null);
          setChainedQuoteContext(null);
          setChainedQuoteCustomerName(ctx?.account?.name || '');
          setShowQuoteModal(true);
        }}
      />
      <ScheduleCallModal isOpen={showScheduleCall} onClose={() => setShowScheduleCall(false)} />
      {showAddToCrm && (
        <AddToCrmModal
          isOpen={showAddToCrm}
          onClose={() => { setShowAddToCrm(false); setChainedCallContext(null); setChainedQuoteContext(null); }}
          quoteForm={fastTrackForm || (chainedCallContext ? {
            customer_name: chainedCallContext.account?.name || '',
            customer_account_number: chainedCallContext.account?.zoho_account_number || '',
            zoho_account_id: chainedCallContext.account?.id || '',
            contact_id: chainedCallContext.contact?.id || '',
            contact_name: chainedCallContext.contact?.name || '',
          } : null)}
          pdfFile={fastTrackPdf || null}
          subtotal={fastTrackSubtotal || 0}
          fastTrack={true}
          onChainToCall={(ctx) => {
            setChainedQuoteContext(ctx);
            setShowAddToCrm(false);
            setShowAddCall(true);
          }}
        />
      )}
      <ContactNotesTasksModal
        isOpen={showContactNotesTasks}
        onClose={() => setShowContactNotesTasks(false)}
      />
      <SearchBarbourModal isOpen={showSearchBarbour} onClose={() => setShowSearchBarbour(false)} />
      <SearchBarbourModal
        isOpen={showSearchBarbourV2}
        onClose={() => setShowSearchBarbourV2(false)}
        apiPath="/barbour/search-v2"
        title="Barbour Search V2"
        subtitle="Find Barbour ABI projects via the Zoho CRM function"
        testIdPrefix="search-barbour-v2"
      />
      {showQuoteModal && Quotes.Modal && (
        <Quotes.Modal
          isOpen={showQuoteModal}
          onClose={() => { setShowQuoteModal(false); setChainedQuoteCustomerName(''); }}
          mode="recent"
          prefillCustomer={chainedQuoteCustomerName}
        />
      )}
      </InBody>
    </>
  );
};

export default LogCallDropdownPill;
